import { faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Badge } from '@themesberg/react-bootstrap';
import moment from 'moment';
import React, { useMemo, useState } from 'react'

const formatVnd = (value) => {
  const numericValue = Number(value || 0)
  if (!Number.isFinite(numericValue)) return '-'
  return numericValue.toLocaleString('vi-VN')
}

const groupTicketsByChannel = (tickets = []) => {
  const map = new Map()
  for (const ticket of tickets || []) {
    const channelName = ticket?.channelName || ticket?.channel?.name || ticket?.channel || ticket?.channelId || 'N/A'
    const amount = Number(ticket?.amount || 0)
    map.set(channelName, (map.get(channelName) || 0) + (Number.isFinite(amount) ? amount : 0))
  }
  return Array.from(map.entries())
    .map(([channel, amount]) => ({ channel, amount }))
    .sort((a, b) => b.amount - a.amount)
}

const resolveWinningSummary = (tickets = []) => {
  if (!Array.isArray(tickets) || tickets.length === 0) return null

  const totalReward = tickets.reduce((sum, t) => {
    const rewardValue = Number(t?.reward || 0)
    return sum + (Number.isFinite(rewardValue) ? rewardValue : 0)
  }, 0)

  const wonAmount = tickets.reduce((sum, t) => {
    const status = Number(t?.status)
    if (status !== 4) return sum
    const amount = Number(t?.amount || 0)
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)

  const notWonAmount = tickets.reduce((sum, t) => {
    const status = Number(t?.status)
    if (status !== 5) return sum
    const amount = Number(t?.amount || 0)
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)

  const pendingAmount = tickets.reduce((sum, t) => {
    const status = Number(t?.status)
    if ([4, 5].includes(status)) return sum
    const amount = Number(t?.amount || 0)
    return sum + (Number.isFinite(amount) ? amount : 0)
  }, 0)

  return { totalReward, wonAmount, notWonAmount, pendingAmount }
}

export const TableRow = (props) => {
  const { user, createdAt, status, tickets, id, images, doneAt, region, totalValue, handleShowOrderDetail } = props;

  const [statusState] = useState(status)

  const datedString = moment(createdAt || '').format('DD/MM/YYYY HH:mm').toLocaleString()

  const doneDateString = doneAt ? moment(doneAt).format('DD/MM/YYYY HH:mm').toLocaleString() : '-'

  let amount = 0;

  for (let index = 0; index < tickets.length; index++) {
    const element = tickets[index];
    amount += element.amount
  }

  const ticketByChannels = useMemo(() => groupTicketsByChannel(tickets), [tickets])

  const winningSummary = useMemo(() => resolveWinningSummary(tickets), [tickets])

  const winningLabel = useMemo(() => {
    if (!winningSummary) return <span className="text-muted small">-</span>

    const { totalReward, wonAmount, notWonAmount, pendingAmount } = winningSummary

    if (wonAmount === 0 && notWonAmount === 0 && pendingAmount === 0) return <span className="text-muted small">-</span>

    return (
      <div className="d-flex flex-column align-items-center gap-1">
        <Badge bg={wonAmount > 0 ? 'success' : 'secondary'} className="px-3 py-2 fw-normal">
          Trúng: {wonAmount}
        </Badge>
        <Badge bg={notWonAmount > 0 ? 'danger' : 'secondary'} className="px-3 py-2 fw-normal">
          Không trúng: {notWonAmount}
        </Badge>
        {pendingAmount > 0 && (
          <Badge bg="warning" text="dark" className="px-3 py-2 fw-normal">
            Chờ KQ: {pendingAmount}
          </Badge>
        )}
        {totalReward > 0 && (
          <div className="text-success small fw-bold mt-1">
            Tiền trúng: {formatVnd(totalReward)}đ
          </div>
        )}
      </div>
    )
  }, [winningSummary])

  const regionLabel = useMemo(() => {
    switch (region) {
      case 'MN':
        return 'Miền Nam'
      case 'MT':
        return 'Miền Trung'
      case 'MB':
        return 'Miền Bắc'
      default:
        return region || '-'
    }
  }, [region])

  const hasImages = Array.isArray(images) && images.some((img) => img && img.length > 0)

  const statusLabel = useMemo(() => {
    if ([1, 2].includes(statusState)) {
      if (hasImages) {
        return <Badge bg="info" className="px-3 py-2 fw-normal">Đã chụp hình</Badge>
      }
      return <Badge bg="warning" text="dark" className="px-3 py-2 fw-normal">Đơn mới</Badge>
    } else if ([3, 4, 5].includes(statusState)) {
      return <Badge bg="success" className="px-3 py-2 fw-normal">Hoàn thành</Badge>
    } else if (statusState === 6) {
      return <Badge bg="danger" className="px-3 py-2 fw-normal">Đã hủy - hoàn tiền</Badge>
    } else {
      return <Badge bg="secondary" className="px-3 py-2 fw-normal">Không xác định</Badge>
    }
  }, [statusState, hasImages])

  const actionTd = useMemo(() => {
    return (
      <Button 
        variant="primary" 
        size="sm" 
        className="px-3"
        onClick={() => handleShowOrderDetail && handleShowOrderDetail(props)}
      >
        Xem vé
        <FontAwesomeIcon icon={faEye} className="ms-2" />
      </Button>
    )
  }, [handleShowOrderDetail, props])

  return (
    <tr className="align-middle">
      <td className="text-center py-3">
        {actionTd}
      </td>
      <td className="text-start fw-bold text-primary small">#{id}</td>
      <td className="text-end">
        <span className="fw-bold text-success">{formatVnd(totalValue)}đ</span>
      </td>
      <td className="text-center">
        <Badge bg="info" className="px-3 py-2 fw-normal">{regionLabel}</Badge>
      </td>
      <td style={{ minWidth: 220 }}>
        {ticketByChannels.length > 0 ? (
          ticketByChannels.slice(0, 5).map((item) => (
            <div key={`${id}-${item.channel}`} className="small mb-1">
              <span className="text-muted">{item.channel}:</span>{' '}
              <span className="fw-bold text-dark">{item.amount}</span>
            </div>
          ))
        ) : (
          <span className="text-muted small">-</span>
        )}
        {ticketByChannels.length > 5 ? (
          <div className="text-muted small">+{ticketByChannels.length - 5} đài khác</div>
        ) : null}
      </td>
      <td className="text-center" style={{ minWidth: 170 }}>{winningLabel}</td>
      <td className="text-center">{statusLabel}</td>
      <td className="text-center">
        <div className="fw-bold text-dark small">{(user && user.name) || "-"}</div>
        <div className="text-primary small fw-bold">{(user && user.phone) || "-"}</div>
      </td>
      <td className="text-center">
        <span className="fw-bold text-primary">{amount}</span>
      </td>
      <td className="text-center">
        <div className="d-flex gap-2 justify-content-center">
          {images && images[0] && (
            <div
              className="d-inline-block rounded overflow-hidden shadow-sm position-relative"
              style={{ 
                width: "48px", 
                height: "48px", 
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "";
              }}
              onClick={() => props.handleShowImagePreview('Ảnh 1', images[0])}
            >
              <img
                alt="Ảnh vé 1"
                className="w-100 h-100 object-fit-cover"
                src={images[0]}
              />
              <div 
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-0"
                style={{
                  transition: "background-color 0.2s ease",
                  pointerEvents: "none"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.3)";
                }}
              >
              </div>
            </div>
          )}
          {images && images[1] && (
            <div
              className="d-inline-block rounded overflow-hidden shadow-sm position-relative"
              style={{ 
                width: "48px", 
                height: "48px", 
                cursor: "pointer",
                transition: "transform 0.2s ease, box-shadow 0.2s ease"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.1)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.3)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "";
              }}
              onClick={() => props.handleShowImagePreview('Ảnh 2', images[1])}
            >
              <img
                alt="Ảnh vé 2"
                className="w-100 h-100 object-fit-cover"
                src={images[1]}
              />
            </div>
          )}
          {(!images || (!images[0] && !images[1])) && (
            <span className="text-muted small">-</span>
          )}
        </div>
      </td>
      <td className="text-center text-muted small">{datedString}</td>
      <td className="text-center text-muted small">{doneDateString}</td>
    </tr>
  );
};
